import React, { useState, useEffect } from 'react';
import { X, Save, Gem, Package, DollarSign, Tag, Image as ImageIcon } from 'lucide-react';
import api, { API_URL } from '../../services/api';

function CreerBijou({ onClose, onSuccess }) {
  const [step, setStep] = useState(1);
  const [collections, setCollections] = useState([]);
  const [options, setOptions] = useState([]);
  const [prixOr, setPrixOr] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    // Étape 1: Collection
    collection_id: '',
    
    // Étape 2: Type et infos de base
    nom: '',
    categorie: 'bague',
    matiere: 'or',
    reference_base: '',
    
    // Étape 3: Détails et prix
    poids: '',
    poids_reel: '',
    taille_diamant: '',
    prix_achat: '',
    prix_base: '',
    description: '',
    notes_variation: '',
    
    // Étape 4: Options
    options_selectionnees: []
  });

  const [prixCalcule, setPrixCalcule] = useState({
    prix_base: 0,
    prix_options: 0,
    prix_total: 0
  });

  useEffect(() => {
    loadCollections();
    loadOptions();
    loadPrixOr();
  }, []);

  useEffect(() => {
    calculerPrixTotal();
  }, [formData.prix_base, formData.options_selectionnees]);

  const loadCollections = async () => {
    try {
      const response = await api.get(`${API_URL}/collections?actif=true`);
      setCollections(response.data);
    } catch (error) {
      console.error('Erreur chargement collections:', error);
    }
  };

  const loadOptions = async () => {
    try {
      const response = await api.get(`${API_URL}/options?actif=true`);
      setOptions(response.data);
    } catch (error) {
      console.error('Erreur chargement options:', error);
    }
  };

  const loadPrixOr = async () => {
    try {
      const response = await api.get(`${API_URL}/options/prix-or/actuel?titre=18K`);
      setPrixOr(response.data);
    } catch (error) {
      console.error('Erreur chargement prix or:', error);
    }
  };

  const calculerPrixTotal = async () => {
    const prixBase = parseFloat(formData.prix_base) || 0;
    
    if (formData.options_selectionnees.length === 0) {
      setPrixCalcule({
        prix_base: prixBase,
        prix_options: 0,
        prix_total: prixBase
      });
      return;
    }

    try {
      const response = await api.post(`${API_URL}/options/calculer-prix`, {
        prix_base: prixBase,
        options: formData.options_selectionnees
      });
      setPrixCalcule(response.data);
    } catch (error) {
      console.error('Erreur calcul prix:', error);
    }
  };

  const handleOptionToggle = (optionId) => {
    setFormData(prev => ({
      ...prev,
      options_selectionnees: prev.options_selectionnees.includes(optionId)
        ? prev.options_selectionnees.filter(id => id !== optionId)
        : [...prev.options_selectionnees, optionId]
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      // 1. Créer le bijou unique
      const bijouData = {
        reference_base: formData.reference_base,
        nom: formData.nom,
        categorie: formData.categorie,
        matiere: formData.matiere,
        collection_id: formData.collection_id,
        poids: parseFloat(formData.poids) || 0,
        poids_reel: parseFloat(formData.poids_reel) || parseFloat(formData.poids) || 0,
        taille_diamant: parseFloat(formData.taille_diamant) || null,
        prix_achat: parseFloat(formData.prix_achat) || 0,
        prix_base: parseFloat(formData.prix_base) || 0,
        prix_vente: prixCalcule.prix_total,
        prix_or_actuel: prixOr?.prix_gramme || null,
        description: formData.description,
        notes_variation: formData.notes_variation
      };

      const bijouResponse = await api.post(`${API_URL}/bijoux-uniques/creer-piece`, bijouData);
      const bijouId = bijouResponse.data.id;

      // 2. Ajouter les options si sélectionnées
      if (formData.options_selectionnees.length > 0) {
        await api.post(`${API_URL}/options/bijou/${bijouId}/ajouter`, {
          options: formData.options_selectionnees
        });
      }

      alert(`Bijou créé avec succès ! Référence: ${bijouResponse.data.reference_complete}`);
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erreur création bijou:', error);
      alert('Erreur lors de la création du bijou: ' + (error.response?.data?.error || error.message));
    } finally {
      setLoading(false);
    }
  };

  const categories = [
    { value: 'bague', label: 'Bague' },
    { value: 'collier', label: 'Collier' },
    { value: 'bracelet', label: 'Bracelet' },
    { value: 'boucles_oreilles', label: "Boucles d'oreilles" },
    { value: 'pendentif', label: 'Pendentif' },
    { value: 'autre', label: 'Autre' }
  ];

  const matieres = [
    { value: 'or', label: 'Or' },
    { value: 'argent', label: 'Argent' },
    { value: 'diamant', label: 'Diamant' },
    { value: 'plaque', label: 'Plaqué' },
    { value: 'autre', label: 'Autre' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white p-6 flex justify-between items-center">
          <div className="flex items-center space-x-3">
            <Gem className="h-8 w-8" />
            <div>
              <h2 className="text-2xl font-bold">Créer un Nouveau Bijou</h2>
              <p className="text-sm opacity-90">Étape {step} sur 4</p>
            </div>
          </div>
          <button onClick={onClose} className="hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition">
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="bg-gray-100 h-2">
          <div 
            className="bg-gradient-to-r from-purple-600 to-blue-600 h-full transition-all duration-300"
            style={{ width: `${(step / 4) * 100}%` }}
          />
        </div>

        <div className="p-6 space-y-6">
          {/* ÉTAPE 1: COLLECTION */}
          {step === 1 && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-800 flex items-center space-x-2">
                <Package className="h-6 w-6 text-purple-600" />
                <span>Choisissez une Collection</span>
              </h3>
              
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {collections.map(collection => (
                  <button
                    key={collection.id}
                    onClick={() => setFormData({ ...formData, collection_id: collection.id })}
                    className={`p-4 rounded-lg border-2 transition-all ${
                      formData.collection_id === collection.id
                        ? 'border-purple-600 bg-purple-50 shadow-md'
                        : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="text-center">
                      <h4 className="font-bold text-lg">{collection.nom}</h4>
                      <p className="text-sm text-gray-600 mt-1">{collection.description}</p>
                    </div>
                  </button>
                ))}
              </div>

              {!formData.collection_id && (
                <p className="text-sm text-gray-500 italic">Sélectionnez une collection pour continuer</p>
              )}
            </div>
          )}

          {/* ÉTAPE 2: TYPE ET INFOS DE BASE */}
          {step === 2 && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-800 flex items-center space-x-2">
                <Tag className="h-6 w-6 text-purple-600" />
                <span>Type et Informations de Base</span>
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Référence de base *
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: BAG-001"
                    value={formData.reference_base}
                    onChange={(e) => setFormData({ ...formData, reference_base: e.target.value.toUpperCase() })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom du bijou *
                  </label>
                  <input
                    type="text"
                    placeholder="Ex: Bague Solitaire"
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Catégorie *
                  </label>
                  <select
                    value={formData.categorie}
                    onChange={(e) => setFormData({ ...formData, categorie: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    {categories.map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Matière *
                  </label>
                  <select
                    value={formData.matiere}
                    onChange={(e) => setFormData({ ...formData, matiere: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  >
                    {matieres.map(mat => (
                      <option key={mat.value} value={mat.value}>{mat.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* ÉTAPE 3: DÉTAILS ET PRIX */}
          {step === 3 && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-800 flex items-center space-x-2">
                <DollarSign className="h-6 w-6 text-purple-600" />
                <span>Détails et Prix</span>
              </h3>

              {/* Prix de l'or actuel */}
              {prixOr && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-yellow-800">
                    💰 Prix de l'or actuel: <span className="font-bold">{prixOr.prix_gramme} DH/g</span> ({prixOr.titre})
                  </p>
                  <p className="text-xs text-yellow-700 mt-1">
                    Ce prix sera enregistré avec le bijou pour traçabilité
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Poids théorique (g)
                  </label>
                  <input
                    type="text" inputMode="decimal" onFocus={e => e.target.select()}
                    step="0.01"
                    placeholder="Ex: 3.5"
                    value={formData.poids}
                    onChange={(e) => setFormData({ ...formData, poids: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Poids réel (g)
                  </label>
                  <input
                    type="text" inputMode="decimal" onFocus={e => e.target.select()}
                    step="0.01"
                    placeholder="Ex: 3.6"
                    value={formData.poids_reel}
                    onChange={(e) => setFormData({ ...formData, poids_reel: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Taille diamant (carats)
                  </label>
                  <input
                    type="text" inputMode="decimal" onFocus={e => e.target.select()}
                    step="0.01"
                    placeholder="Ex: 0.5"
                    value={formData.taille_diamant}
                    onChange={(e) => setFormData({ ...formData, taille_diamant: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prix d'achat (DH)
                  </label>
                  <input
                    type="text" inputMode="decimal" onFocus={e => e.target.select()}
                    step="0.01"
                    placeholder="Ex: 3000"
                    value={formData.prix_achat}
                    onChange={(e) => setFormData({ ...formData, prix_achat: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prix de base (DH) *
                  </label>
                  <input
                    type="text" inputMode="decimal" onFocus={e => e.target.select()}
                    step="0.01"
                    placeholder="Ex: 5000"
                    value={formData.prix_base}
                    onChange={(e) => setFormData({ ...formData, prix_base: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Prix sans les options (les options seront ajoutées à l'étape suivante)
                  </p>
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description
                  </label>
                  <textarea
                    rows="3"
                    placeholder="Description du bijou..."
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes de variation
                  </label>
                  <textarea
                    rows="2"
                    placeholder="Notes spécifiques sur cette pièce..."
                    value={formData.notes_variation}
                    onChange={(e) => setFormData({ ...formData, notes_variation: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>
            </div>
          )}

          {/* ÉTAPE 4: OPTIONS */}
          {step === 4 && (
            <div className="space-y-4">
              <h3 className="text-xl font-bold text-gray-800 flex items-center space-x-2">
                <Package className="h-6 w-6 text-purple-600" />
                <span>Options et Services</span>
              </h3>

              <p className="text-sm text-gray-600">
                Cochez les options souhaitées. Le prix sera ajouté automatiquement au prix de base.
              </p>

              <div className="space-y-3">
                {options.map(option => (
                  <label
                    key={option.id}
                    className={`flex items-center justify-between p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      formData.options_selectionnees.includes(option.id)
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        checked={formData.options_selectionnees.includes(option.id)}
                        onChange={() => handleOptionToggle(option.id)}
                        className="w-5 h-5 text-green-600 rounded focus:ring-2 focus:ring-green-500"
                      />
                      <div>
                        <p className="font-medium text-gray-800">{option.nom}</p>
                        <p className="text-sm text-gray-600">{option.description}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-lg font-bold text-green-600">+{option.prix} DH</p>
                    </div>
                  </label>
                ))}
              </div>

              {/* Calcul du prix total */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Prix de base:</span>
                  <span className="font-medium">{prixCalcule.prix_base.toFixed(2)} DH</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-700">Options:</span>
                  <span className="font-medium text-green-600">+{prixCalcule.prix_options.toFixed(2)} DH</span>
                </div>
                <div className="border-t border-blue-300 pt-2 flex justify-between">
                  <span className="font-bold text-lg text-gray-800">Prix total:</span>
                  <span className="font-bold text-2xl text-blue-600">{prixCalcule.prix_total.toFixed(2)} DH</span>
                </div>
              </div>
            </div>
          )}

          {/* Buttons */}
          <div className="flex justify-between pt-4 border-t">
            <button
              onClick={() => setStep(Math.max(1, step - 1))}
              disabled={step === 1}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition"
            >
              Précédent
            </button>

            {step < 4 ? (
              <button
                onClick={() => setStep(step + 1)}
                disabled={
                  (step === 1 && !formData.collection_id) ||
                  (step === 2 && (!formData.reference_base || !formData.nom)) ||
                  (step === 3 && !formData.prix_base)
                }
                className="px-6 py-2 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                Suivant
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading}
                className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg hover:from-green-700 hover:to-green-800 disabled:opacity-50 disabled:cursor-not-allowed transition"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Création...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-5 w-5" />
                    <span>Créer le bijou</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default CreerBijou;
